import type { PromiseCompletion } from "../classes/PromiseCompletion";
import { toastService } from "./toastService";

export interface AjaxOptions extends RequestInit {
  loader?: PromiseCompletion;
  onRateLimit?: () => void; // Optional callback for rate limit handling
  suppressToast?: boolean;  // Suppress automatic error toast for this request
  suppress404?: boolean;    // Suppress error toast specifically for 404 responses
}

let refreshTokenPromise: Promise<void> | null = null;

// Define a custom HttpError type if needed to raise specific HTTP errors (inheriting from Error)
export class HttpError extends Error {
  public status: number;
  public statusText: string;
  public response: Response;

  constructor(response: Response, message?: string) {
    super(message || `HTTP error! status: ${response.status}`);
    this.name = "HttpError";
    this.status = response.status;
    this.statusText = response.statusText;
    this.response = response;
  }

  // override toString method to include status code and status text
  public toString(): string {
    return `${this.name}: ${this.message} (status: ${this.status} ${this.statusText})`;
  }
}

class apiService {

  public async refreshToken(loader?: PromiseCompletion, autoRedirect?: boolean): Promise<void> {
    const refreshRequest = fetch('/auth/refresh', {
      method: 'POST'
    });
    if (loader) {
      loader.subscribe(refreshRequest);
    }
    const refreshResponse = await refreshRequest;
    if (!refreshResponse.ok && autoRedirect) {
      if (refreshResponse.status === 401) {
        window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
      }
      throw new Error(`Failed to refresh token. Status: ${refreshResponse.status}, online: ${navigator.onLine}`);
    }
  }

  private async _execFetch(url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', options?: AjaxOptions): Promise<Response> {
    const headers = {
      ...options?.headers,
    };

    const request = fetch(url, {
      method,
      headers,
      redirect: 'manual',
      body: options?.body
    });

    options?.loader?.subscribe(request);

    const response = await request;

    if (response.status === 401) {

      if (!refreshTokenPromise){
        refreshTokenPromise = new Promise(async (resolve, reject) => {
          try {
            const usernameCookie = document.cookie.split('; ').find(row => row.startsWith('username='));
            if (usernameCookie) {
              //try to refresh the token
              await this.refreshToken(options?.loader, true);
              resolve();
            } else {
              reject(new Error('No username cookie found.'));
            }
          } catch (error) {
            reject(error);
          } finally {
            refreshTokenPromise = null; // Reset the promise after completion
          }
        });
      }

      await refreshTokenPromise;
      return this._execFetch(url, method, options); // Retry the request after refreshing the token
    }

    if (response.status === 429 && options?.onRateLimit) {
      options.onRateLimit();
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    if (!response.ok) {
      const body = await response.text();
      const error = new HttpError(response, body);
      if (!options?.suppressToast && !(options?.suppress404 && response.status === 404)) {
        this._showErrorToast(response.status, body);
      }
      throw error;
    }

    return response;
  }

  private _showErrorToast(status: number, body: string) {
    let title: string;
    let message: string | undefined;

    if (status >= 500) {
      title = `Server error (${status})`;
      const parsed = this._tryParseErrorBody(body);
      message = parsed ?? 'An unexpected server error occurred. Please try again.';
    } else if (status === 400) {
      title = 'Bad request';
      message = this._tryParseErrorBody(body) ?? 'The request could not be processed.';
    } else if (status === 403) {
      title = 'Access denied';
      message = this._tryParseErrorBody(body) ?? 'You do not have permission to perform this action.';
    } else if (status === 404) {
      title = 'Not found';
      message = this._tryParseErrorBody(body) ?? 'The requested resource was not found.';
    } else {
      title = `Request failed (${status})`;
      message = this._tryParseErrorBody(body);
    }

    const type = status >= 500 ? 'error' : 'warning';
    toastService.show(type, title, message);
  }

  private _tryParseErrorBody(body: string): string | undefined {
    if (!body) return undefined;
    try {
      const json = JSON.parse(body);
      return json.message ?? json.error ?? json.title ?? undefined;
    } catch {
      // Not JSON — use plain text if reasonably short
      return body.length <= 200 ? body : undefined;
    }
  }

  public async get<T>(url: string, options?: AjaxOptions): Promise<T> {
    const response = await this._execFetch(url, 'GET', options);

    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }

  public async post<T = unknown>(url: string, data?: unknown, options?: AjaxOptions): Promise<T> {
    const response = await this._execFetch(url, 'POST', {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      loader: options?.loader
    });

    if (!response.ok) {
      throw new HttpError(response, await response.text());
    }

    const contentLength = response.headers.get('Content-Length');
    if (contentLength === '0' || response.status === 204) {
      // No content to parse
      return null as T;
    }
    
    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }
  
  public async put<T = unknown>(url: string, data?: unknown, options?: AjaxOptions): Promise<T> {
    const response = await this._execFetch(url, 'PUT', {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      loader: options?.loader
    });

    if (!response.ok) {
      throw new HttpError(response, await response.text());
    }

    const contentLength = response.headers.get('Content-Length');
    if (contentLength === '0' || response.status === 204) {
      // No content to parse
      return null as T;
    }

    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }

  public async delete(url: string, options?: AjaxOptions): Promise<void> {
    const response = await this._execFetch(url, 'DELETE', options);

    if (!response.ok) {
      throw new HttpError(response, await response.text());
    }
  }
}

export default new apiService();